// frontend/src/pages/Login.js – NextScenes Login (email verification aware + Google/Facebook/Apple sign-in)
// Build: 2026-04-29-login-visual-alignment-v1
//
// Preserves:
// - bilingual UI via lang prop
// - invite-token continuation / group invite acceptance
// - soft CSRF prime
// - resend verification flow
// - forgot-password flow
//
// Fixes:
// - proper refreshMe usage after Google auth
// - removes invalid hook call inside handler
// - Google login now updates frontend auth state before redirect

import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import { useUser } from "../UserContext";
import { api } from "../api";

const POST_LOGIN_TARGET_KEY = "ns.postLoginTarget";
const PENDING_PROJECT_CATEGORY_KEY = "ns.nav.pendingProjectCategory";
const INVITE_KIND_KEY = "ns.inviteKind";
const INVITE_CODE_KEY = "ns.inviteCodeForGoogle";
const WRITER_ROLE_KEY = "ns.roleChoiceForGoogle";
const WRITER_AGREE_KEY = "ns.writerAgreementForGoogle";

function normalizeLoginError(msg, lang = "en") {
  const raw = String(msg || "");
  const m = raw.toLowerCase();
  const isFr = String(lang || "en").toLowerCase().startsWith("fr");

  if (m.includes("csrf check failed")) {
    return isFr
      ? "Échec du contrôle de sécurité. Actualisez la page puis réessayez."
      : "Security check failed. Please refresh the page and try again.";
  }
  if (m.includes("email not verified")) {
    return isFr
      ? "Email non vérifié. Vérifiez votre boîte de réception, puis confirmez votre email avant de vous connecter."
      : "Email not verified. Please check your inbox, then verify before logging in.";
  }
  if (m.includes("invalid credentials")) {
    return isFr
      ? "Nom d’utilisateur (ou email) ou mot de passe invalide."
      : "Invalid username (or email) or password.";
  }
  if (m.includes("unauthorized")) {
    return isFr
      ? "Session introuvable. Veuillez vous reconnecter."
      : "Session not found. Please log in again.";
  }
  if (m.includes("google sign-in failed") || m.includes("google sign-in could not be completed")) {
    return isFr
      ? "La connexion Google n’a pas pu aboutir. Réessayez, ou utilisez votre nom d’utilisateur/email et mot de passe."
      : "Google sign-in could not be completed. Please try again, or use username/email and password.";
  }
  if (m.includes("google login is not configured")) {
    return isFr
      ? "La connexion Google n’est pas encore configurée."
      : "Google sign-in is not configured yet.";
  }
  if (m.includes("facebook sign-in failed")) {
    return isFr ? "La connexion Facebook a échoué." : "Facebook sign-in failed.";
  }
  if (m.includes("facebook login is not configured")) {
    return isFr
      ? "La connexion Facebook n’est pas encore configurée."
      : "Facebook sign-in is not configured yet.";
  }
  if (m.includes("facebook account email missing") || m.includes("facebook account email missing or invalid")) {
    return isFr
      ? "Facebook n’a pas fourni d’email vérifié. Vérifiez votre compte Facebook ou utilisez une autre méthode."
      : "Facebook did not provide a verified email. Please check your Facebook account or use another method.";
  }
  if (m.includes("account already exists with this email") || m.includes("facebook linking can be added") || m.includes("facebook linking will be added") || m.includes("not linked to this nextscenes account")) {
    return isFr
      ? "Un compte NextScenes existe déjà avec cet email. Connectez-vous d’abord avec votre méthode habituelle, puis liez Facebook depuis le profil. L’email Facebook doit correspondre à l’email NextScenes."
      : "A NextScenes account already exists with this email. Please log in with your usual method first, then link Facebook from Profile. The Facebook email must match your NextScenes email.";
  }
  if (m.includes("apple sign-in") || m.includes("apple account") || m.includes("apple identity")) {
    if (m.includes("already exists with this email")) {
      return isFr
        ? "Un compte NextScenes existe déjà avec cet email. Connectez-vous d’abord avec votre méthode habituelle. La liaison Apple pourra être ajoutée ensuite."
        : "A NextScenes account already exists with this email. Please log in first with your usual method. Apple linking can be added later.";
    }
    if (m.includes("not configured")) {
      return isFr ? "La connexion Apple n’est pas encore configurée." : "Apple sign-in is not configured yet.";
    }
    return isFr
      ? "La connexion Apple n’a pas pu aboutir. Réessayez, ou utilisez votre nom d’utilisateur/email et mot de passe."
      : "Apple sign-in could not be completed. Please try again, or use username/email and password.";
  }
  if (m.includes("this account uses apple sign-in")) {
    return isFr
      ? "Ce compte utilise la connexion Apple. Veuillez continuer avec Apple."
      : "This account uses Apple sign-in. Please continue with Apple.";
  }

  if (m.includes("invite-only") || m.includes("invite only") || m.includes("invite code")) {
    return isFr
      ? "L’inscription est réservée aux invités pour le moment. Veuillez fournir un code d’invitation."
      : "Registration is invite-only right now. Please provide an invite code.";
  }
  if (m.includes("writer/moderator agreement") || m.includes("writer agreement is required")) {
    return isFr
      ? "Pour vous inscrire avec un rôle d’écriture, vous devez accepter les conditions Writer/Moderator."
      : "To register with writing access, you must accept the Writer/Moderator conditions.";
  }
  if (m.includes("this account uses facebook sign-in")) {
    return isFr
      ? "Ce compte utilise la connexion Facebook. Veuillez continuer avec Facebook."
      : "This account uses Facebook sign-in. Please continue with Facebook.";
  }
  if (m.includes("this account uses google sign-in")) {
    return isFr
      ? "Ce compte utilise la connexion Google. Veuillez continuer avec Google."
      : "This account uses Google sign-in. Please continue with Google.";
  }

  return raw || (isFr ? "Échec de la connexion." : "Login failed.");
}

function looksLikeEmail(value) {
  const v = String(value || "").trim();
  return Boolean(v && v.includes("@"));
}

function safeConsumePostLoginTarget() {
  try {
    const p = String(window.localStorage.getItem(POST_LOGIN_TARGET_KEY) || "").trim();
    if (!p) return "";
    window.localStorage.removeItem(POST_LOGIN_TARGET_KEY);
    return p;
  } catch {
    return "";
  }
}

function chooseDefaultLanding(role) {
  const r = String(role || "").toLowerCase();
  return ["writer", "power", "moderator", "admin"].includes(r) ? "projectHub" : "storylines";
}

function safeReadLocal(key) {
  try {
    return String(window.localStorage.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

export default function Login({ onNavigate, onNavigatePage, lang }) {
  const {
    login,
    logout,
    resendVerification,
    refreshMe,
    isAuthenticated,
    user,
    booting,
    role,
    isWriter,
    isAdmin,
  } = useUser();

  const isFr = String(lang || "en").toLowerCase().startsWith("fr");
  const tr = (en, fr) => (isFr ? fr : en);

  function roleLabel(roleRaw) {
    const r = String(roleRaw || "").toLowerCase();
    if (r === "admin") return tr("Admin", "Admin");
    if (r === "writer") return tr("Writer", "Écrivain");
    return tr("User", "Utilisateur");
  }

  const nav =
    typeof onNavigate === "function"
      ? onNavigate
      : typeof onNavigatePage === "function"
        ? onNavigatePage
        : null;

  const BACKEND_PUBLIC_URL = useMemo(() => {
    const raw = String(process.env.REACT_APP_BACKEND_PUBLIC_URL || "").trim();
    const fallback = "https://api.nextscenes.org";
    return (raw || fallback).replace(/\/+$/, "");
  }, []);

  const GOOGLE_CLIENT_ID = useMemo(() => {
    return String(process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();
  }, []);

  const FACEBOOK_APP_ID = useMemo(() => {
    return String(process.env.REACT_APP_FACEBOOK_APP_ID || "").trim();
  }, []);

  const APPLE_SIGNIN_ENABLED = useMemo(() => {
    return String(process.env.REACT_APP_APPLE_SIGNIN_ENABLED || "true").trim().toLowerCase() !== "false";
  }, []);

  const [loginName, setLoginName] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [showVerifyHelp, setShowVerifyHelp] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");

  const [showForgot, setShowForgot] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  const [googleReady, setGoogleReady] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleLoadProblem, setGoogleLoadProblem] = useState("");
  const [googleReloadKey, setGoogleReloadKey] = useState(0);
  const [facebookReady, setFacebookReady] = useState(false);
  const [facebookBusy, setFacebookBusy] = useState(false);
  const [facebookLoadProblem, setFacebookLoadProblem] = useState("");
  const [facebookReloadKey, setFacebookReloadKey] = useState(0);
  const [appleBusy, setAppleBusy] = useState(false);

  const googleBtnRef = useRef(null);

  const [inviteToken, setInviteToken] = useState("");
  const [inviteKind, setInviteKind] = useState("");

  /*
   * Capture the invitation continuation pair before Login effects run.
   *
   * Project InviteAccept stores both the token and kind before handing
   * off to Login. Some navigation paths can preserve inviteToken while
   * dropping inviteKind=project from the URL. The pre-effect pair lets
   * Login recover project identity only when the stored token is the
   * exact same token, while still preventing a new Story token from
   * inheriting stale project identity.
   */
  const initialInviteContinuationRef =
    useRef({
      token:
        safeReadLocal(
          "ns.inviteToken"
        ),

      kind:
        safeReadLocal(
          INVITE_KIND_KEY
        )
          .trim()
          .toLowerCase(),
    });

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      const t = String(sp.get("inviteToken") || "").trim();
      if (t) {
        window.localStorage.setItem("ns.inviteToken", t);
        setInviteToken(t);
        return;
      }
      const stored = String(window.localStorage.getItem("ns.inviteToken") || "").trim();
      if (stored) setInviteToken(stored);
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const sp =
        new URLSearchParams(
          window.location.search || ""
        );

      const fromQuery =
        String(
          sp.get("inviteKind") || ""
        )
          .trim()
          .toLowerCase();

      const inviteTokenFromQuery =
        String(
          sp.get("inviteToken") || ""
        ).trim();

      if (
        inviteTokenFromQuery &&
        fromQuery !== "project"
      ) {
        const initialToken =
          String(
            initialInviteContinuationRef
              .current?.token ||
            ""
          ).trim();

        const initialKind =
          String(
            initialInviteContinuationRef
              .current?.kind ||
            ""
          )
            .trim()
            .toLowerCase();

        const sameStoredProjectInvite =
          initialKind ===
            "project" &&
          initialToken ===
            inviteTokenFromQuery;

        if (
          sameStoredProjectInvite
        ) {
          window.localStorage.setItem(
            INVITE_KIND_KEY,
            "project"
          );

          setInviteKind(
            "project"
          );

          return;
        }

        window.localStorage.removeItem(
          INVITE_KIND_KEY
        );

        setInviteKind("");

        return;
      }

      if (
        fromQuery === "project"
      ) {
        window.localStorage.setItem(
          INVITE_KIND_KEY,
          "project"
        );

        setInviteKind(
          "project"
        );

        return;
      }

      const stored =
        String(
          window.localStorage.getItem(
            INVITE_KIND_KEY
          ) || ""
        )
          .trim()
          .toLowerCase();

      if (
        stored === "project"
      ) {
        setInviteKind(
          "project"
        );
      }
    } catch {}
  }, []);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      if (sp.get("apple_error")) {
        const msg = sp.get("message") || "Apple sign-in could not be completed.";
        setErr(normalizeLoginError(msg, isFr ? "fr" : "en"));
        window.history.replaceState({}, "", "/login");
      }
    } catch {}
  }, [isFr]);

  const displayIdentity = useMemo(() => {
    return user?.name || user?.username || user?.email || "";
  }, [user]);

  const trimmedLoginName = useMemo(() => String(loginName || "").trim(), [loginName]);

  const canSubmit = useMemo(() => {
    if (busy || googleBusy || facebookBusy || appleBusy) return false;
    if (!trimmedLoginName) return false;
    if (!String(password || "").trim()) return false;
    return true;
  }, [busy, googleBusy, facebookBusy, appleBusy, trimmedLoginName, password]);

  function redirectAfterLogin(resolvedRole) {
    if (!nav) return;

    const pendingInvite =
      String(inviteToken || "").trim() ||
      (function () {
        try {
          return String(window.localStorage.getItem("ns.inviteToken") || "").trim();
        } catch {
          return "";
        }
      })();

    if (pendingInvite) {
      try {
        const pendingInviteKind =
          String(
            inviteKind ||
            (function () {
              try {
                return String(
                  window.localStorage.getItem(
                    INVITE_KIND_KEY
                  ) || ""
                );
              } catch {
                return "";
              }
            })()
          )
            .trim()
            .toLowerCase();

        try {
          window.localStorage.removeItem(
            "ns.inviteToken"
          );

          window.localStorage.removeItem(
            INVITE_KIND_KEY
          );
        } catch {}

        const inviteAcceptPath =
          pendingInviteKind === "project"
            ? `/api/project-invitations/${encodeURIComponent(
                pendingInvite
              )}/accept`
            : `/api/stories/group/invite/${encodeURIComponent(
                pendingInvite
              )}/accept`;

        window.location.assign(
          `${BACKEND_PUBLIC_URL}${inviteAcceptPath}`
        );

        return;
      } catch {}
    }

    const target = safeConsumePostLoginTarget();
    let category = "";
    try {
      const pending = String(window.localStorage.getItem(PENDING_PROJECT_CATEGORY_KEY) || "");
      if (["story", "children", "textbook", "screenplay"].includes(pending)) category = pending;
      window.localStorage.removeItem(PENDING_PROJECT_CATEGORY_KEY);
    } catch {}
    if (target) {
      if (target === "projectHub" && category) nav("projectHub", { projectCategory: category });
      else nav(target);
      return;
    }

    nav(chooseDefaultLanding(resolvedRole));
  }

  function retryGoogleSignInLoad() {
    setGoogleReady(false);
    setGoogleLoadProblem("");

    try {
      const existing = document.getElementById("google-identity-services");
      if (existing && !window.google?.accounts?.id) existing.remove();
    } catch {}

    setGoogleReloadKey((n) => n + 1);
  }

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setGoogleReady(false);
      setGoogleLoadProblem("not_configured");
      return;
    }

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const maxAttempts = 48; // about 12 seconds
    const scriptId = "google-identity-services";

    setGoogleReady(false);
    setGoogleLoadProblem("");

    function stopTimer() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function markProblem(kind) {
      if (cancelled) return;
      setGoogleReady(false);
      setGoogleLoadProblem(kind);
    }

    function renderGoogleButton() {
      if (cancelled) return true;
      if (!window.google?.accounts?.id) return false;
      if (!googleBtnRef.current) return false;

      try {
        googleBtnRef.current.innerHTML = "";

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
          locale: isFr ? "fr" : "en",
        });

        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "outline",
          size: "large",
          shape: "rectangular",
          // Match the real container width on phones; leave authentication unchanged.
          width: Math.max(190, Math.min(380, Math.floor(googleBtnRef.current?.getBoundingClientRect().width || 380), Math.floor((googleBtnRef.current?.parentElement?.clientWidth || 380) - 20), Math.floor(window.innerWidth - 96))),
          text: "continue_with",
          locale: isFr ? "fr" : "en",
        });

        setGoogleReady(true);
        setGoogleLoadProblem("");
        return true;
      } catch (err) {
        console.warn("Google sign-in button failed to render:", err);
        markProblem("failed");
        return true;
      }
    }

    function waitForGoogle() {
      if (renderGoogleButton()) return;

      stopTimer();
      timer = window.setInterval(() => {
        attempts += 1;

        if (renderGoogleButton()) {
          stopTimer();
          return;
        }

        if (attempts >= maxAttempts) {
          stopTimer();
          markProblem("slow");
        }
      }, 250);
    }

    const existing = document.getElementById(scriptId);

    if (!existing) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = () => waitForGoogle();
      script.onerror = () => markProblem("failed");
      document.body.appendChild(script);
    } else {
      waitForGoogle();
    }

    return () => {
      cancelled = true;
      stopTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GOOGLE_CLIENT_ID, isFr, googleReloadKey]);

  async function handleGoogleCredential(googleResponse) {
    const credential = String(googleResponse?.credential || "").trim();
    if (!credential) {
      setErr(tr("Google sign-in could not be completed. Please try again, or use username/email and password.", "La connexion Google n’a pas pu aboutir. Réessayez, ou utilisez votre nom d’utilisateur/email et mot de passe."));
      return;
    }

    setErr("");
    setVerifyMsg("");
    setForgotMsg("");
    setShowForgot(false);
    setShowVerifyHelp(false);
    setGoogleBusy(true);

    try {

      // Sign-in must never inherit a role/consent choice from a prior registration.
      const storedInviteCode = safeReadLocal(INVITE_CODE_KEY);
      const payload = { credential, mode: "login" };

      if (inviteToken) payload.inviteToken = inviteToken;
      if (storedInviteCode) payload.inviteCode = storedInviteCode;

      await api.post("/api/auth/google", payload, {
        withCredentials: true,
      });

      const me = await refreshMe();
      const r = String(me?.role || role || "").toLowerCase();
      const finalRole = r || (isAdmin ? "admin" : isWriter ? "writer" : "user");

      redirectAfterLogin(finalRole);
    } catch (ex) {
      const message = normalizeLoginError(
        ex?.data?.message || ex?.response?.data?.message || ex?.message,
        isFr ? "fr" : "en"
      );
      setErr(message);
    } finally {
      setGoogleBusy(false);
    }
  }

  function retryFacebookSignInLoad() {
    setFacebookReady(false);
    setFacebookLoadProblem("");

    try {
      const existing = document.getElementById("facebook-jssdk");
      if (existing && !window.FB) existing.remove();
    } catch {}

    setFacebookReloadKey((n) => n + 1);
  }

  useEffect(() => {
    if (!FACEBOOK_APP_ID) {
      setFacebookReady(false);
      setFacebookLoadProblem("not_configured");
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const maxAttempts = 48; // about 12 seconds
    const scriptId = "facebook-jssdk";
    const locale = isFr ? "fr_FR" : "en_US";

    setFacebookReady(false);
    setFacebookLoadProblem("");

    function stopTimer() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function markProblem(kind) {
      if (cancelled) return;
      setFacebookReady(false);
      setFacebookLoadProblem(kind);
    }

    function initFacebook() {
      if (cancelled) return true;
      if (!window.FB) return false;

      try {
        window.FB.init({
          appId: FACEBOOK_APP_ID,
          cookie: false,
          xfbml: false,
          version: "v25.0",
        });

        setFacebookReady(true);
        setFacebookLoadProblem("");
        return true;
      } catch (err) {
        console.warn("Facebook sign-in SDK failed to initialize:", err);
        markProblem("failed");
        return true;
      }
    }

    function waitForFacebook() {
      if (initFacebook()) return;

      stopTimer();
      timer = window.setInterval(() => {
        attempts += 1;

        if (initFacebook()) {
          stopTimer();
          return;
        }

        if (attempts >= maxAttempts) {
          stopTimer();
          markProblem("slow");
        }
      }, 250);
    }

    if (!document.getElementById("facebook-root")) {
      const root = document.createElement("div");
      root.id = "facebook-root";
      document.body.appendChild(root);
    }

    const existing = document.getElementById(scriptId);

    if (!existing) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://connect.facebook.net/${locale}/sdk.js`;
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.onload = () => waitForFacebook();
      script.onerror = () => markProblem("failed");
      document.body.appendChild(script);
    } else {
      waitForFacebook();
    }

    return () => {
      cancelled = true;
      stopTimer();
    };
  }, [FACEBOOK_APP_ID, isFr, facebookReloadKey]);

  async function handleFacebookLogin() {
    if (!FACEBOOK_APP_ID || !window.FB) {
      setErr(tr("Facebook sign-in is not configured yet.", "La connexion Facebook n’est pas encore configurée."));
      return;
    }

    setErr("");
    setVerifyMsg("");
    setForgotMsg("");
    setShowForgot(false);
    setShowVerifyHelp(false);
    setFacebookBusy(true);

    window.FB.login(
      (facebookResponse) => {
        (async () => {
          try {
            const authResponse = facebookResponse?.authResponse || {};
            const accessToken = String(authResponse.accessToken || "").trim();
            const userID = String(authResponse.userID || "").trim();

            if (!accessToken) {
              throw new Error("Facebook sign-in failed.");
            }


            // Existing-account login does not carry registration role intentions.
            const storedInviteCode = safeReadLocal(INVITE_CODE_KEY);
            const payload = { accessToken, userID, mode: "login" };

            if (inviteToken) payload.inviteToken = inviteToken;
            if (storedInviteCode) payload.inviteCode = storedInviteCode;

            await api.post("/api/auth/facebook", payload, {
              withCredentials: true,
            });

            const me = await refreshMe();
            const r = String(me?.role || role || "").toLowerCase();
            const finalRole = r || (isAdmin ? "admin" : isWriter ? "writer" : "user");

            redirectAfterLogin(finalRole);
          } catch (ex) {
            const message = normalizeLoginError(
              ex?.data?.message || ex?.response?.data?.message || ex?.message,
              isFr ? "fr" : "en"
            );
            setErr(message);
          } finally {
            setFacebookBusy(false);
          }
        })();
      },
      { scope: "public_profile,email", return_scopes: true }
    );
  }


  async function handleAppleLogin() {
    if (!APPLE_SIGNIN_ENABLED) {
      setErr(tr("Apple sign-in is not configured yet.", "La connexion Apple n’est pas encore configurée."));
      return;
    }

    setErr("");
    setVerifyMsg("");
    setForgotMsg("");
    setShowForgot(false);
    setShowVerifyHelp(false);
    setAppleBusy(true);

    try {
      const url = new URL(`${BACKEND_PUBLIC_URL}/api/auth/apple/start`);
      url.searchParams.set("mode", "login");

      if (inviteToken) url.searchParams.set("inviteToken", inviteToken);
      if (inviteKind === "project") {
        url.searchParams.set(
          "inviteKind",
          "project"
        );
      }

      const storedInviteCode = safeReadLocal(INVITE_CODE_KEY);
      if (storedInviteCode) url.searchParams.set("inviteCode", storedInviteCode);

      window.location.assign(url.toString());
    } catch (ex) {
      setAppleBusy(false);
      setErr(tr("Apple sign-in could not be started.", "La connexion Apple n’a pas pu démarrer."));
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setErr("");
    setVerifyMsg("");
    setForgotMsg("");
    setShowForgot(false);
    setShowVerifyHelp(false);
    setBusy(true);

    try {

      const data = await login({ login: trimmedLoginName, password });

      const me = data && (data.user || data);
      const r = String(me?.role || role || "").toLowerCase();

      const finalRole = r || (isAdmin ? "admin" : isWriter ? "writer" : "user");
      redirectAfterLogin(finalRole);
    } catch (ex) {
      const needsVerification = ex?.data?.needsVerification === true || ex?.response?.data?.needsVerification === true;
      const message = normalizeLoginError(
        ex?.data?.message || ex?.response?.data?.message || ex?.message,
        isFr ? "fr" : "en"
      );

      setErr(message);

      if (needsVerification) {
        setShowVerifyHelp(true);
        setVerifyMsg(
          tr(
            "Your account exists but email verification is still pending.",
            "Votre compte existe, mais la vérification email est toujours en attente."
          )
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleResendVerification() {
    const typed = String(loginName || "").trim();
    if (!looksLikeEmail(typed)) {
      setVerifyMsg(
        tr(
          "Type your email address in the box above, then click Resend verification email.",
          "Saisissez votre adresse email ci-dessus, puis cliquez sur Renvoyer l’email de vérification."
        )
      );
      return;
    }

    setVerifyBusy(true);
    setVerifyMsg("");
    try {
      await resendVerification({ email: typed });
      setVerifyMsg(
        tr(
          "Verification email sent again. Please check your inbox (and spam).",
          "Email de vérification renvoyé. Vérifiez votre boîte de réception (et vos spams)."
        )
      );
    } catch (ex) {
      setVerifyMsg(
        String(
          ex?.data?.message ||
            ex?.response?.data?.message ||
            ex?.message ||
            tr("Failed to resend verification email.", "Échec du renvoi de l’email de vérification.")
        )
      );
    } finally {
      setVerifyBusy(false);
    }
  }

  async function handleForgotPassword() {
    setForgotMsg("");
    const typed = String(loginName || "").trim();

    if (!looksLikeEmail(typed)) {
      setForgotMsg(
        tr(
          "Please type your email address in the box above (must include @), then click Send reset link.",
          "Saisissez votre adresse email ci-dessus (doit contenir @), puis cliquez sur Envoyer le lien de réinitialisation."
        )
      );
      return;
    }

    setForgotBusy(true);
    try {
      await api.post("/api/auth/forgot-password", { email: typed });
      setForgotMsg(
        tr(
          "If the account exists, a reset link has been sent to your email.",
          "Si le compte existe, un lien de réinitialisation a été envoyé à votre email."
        )
      );
    } catch (ex) {
      setForgotMsg(
        String(
          ex?.data?.message ||
            ex?.response?.data?.message ||
            ex?.message ||
            tr("Failed to start password reset.", "Impossible de démarrer la réinitialisation du mot de passe.")
        )
      );
    } finally {
      setForgotBusy(false);
    }
  }

  function goWriterOrHome() {
    if (!nav) return;
    const r = String(user?.role || role || "").toLowerCase();
    if (r === "admin") nav("admin");
    else if (r === "writer" || isWriter) nav("projectHub");
    else nav("home");
  }

  function handleCancel() {
    if (nav) nav("home");
  }

  const loginHeroBackgroundUrl = `${process.env.PUBLIC_URL || ""}/images/storyhub/storyhub-background.png`;
  const loginPageStyle = {
    "--ns-login-bg-image": `url("${loginHeroBackgroundUrl}")`,
  };

  if (booting) {
    return (
      <div className="page ns-login-page" style={loginPageStyle}>
        <div className="ns-login-shell ns-login-shell-compact">
          <section className="ns-login-hero" aria-label={tr("Checking session", "Vérification de session")}>
            <div>
              <div className="ns-login-eyebrow">{tr("NextScenes Login", "Connexion NextScenes")}</div>
              <h1>{tr("Checking session…", "Vérification de la session…")}</h1>
              <p>{tr("One moment while NextScenes confirms your session.", "Un instant pendant que NextScenes confirme votre session.")}</p>
            </div>
          </section>
        </div>
      </div>
    );
  }

  // An in-progress sign-in keeps the current form mounted until navigation
  // completes; don't flash the "already logged in" panel in between pages.
  if (isAuthenticated && !(busy || googleBusy || facebookBusy || appleBusy)) {
    return (
      <div className="page ns-login-page" style={loginPageStyle}>
        <div className="ns-login-shell ns-login-shell-compact">
          <section className="ns-login-hero" aria-label={tr("Active session", "Session active")}>
            <div>
              <div className="ns-login-eyebrow">{tr("NextScenes Login", "Connexion NextScenes")}</div>
              <h1>{tr("You are already logged in", "Vous êtes déjà connecté")}</h1>
              <p>{tr("Your session is active. Continue to your working space or return home.", "Votre session est active. Continuez vers votre espace de travail ou retournez à l’accueil.")}</p>
            </div>
          </section>

          <div className="ns-login-card ns-login-status-card">
            <p>
              {tr("Logged in as", "Connecté en tant que")} <b>{displayIdentity}</b>
            </p>
            <p className="ns-login-muted" style={{ marginTop: 8 }}>
              {tr("Role:", "Rôle :")} <b>{roleLabel(user?.role || role)}</b>
            </p>

            <div className="ns-login-actions">
              <button className="btn" type="button" onClick={goWriterOrHome}>
                {tr("Continue", "Continuer")}
              </button>

              <button className="btn secondary" type="button" onClick={logout}>
                {tr("Log out", "Déconnexion")}
              </button>

              <button className="linkBtn ns-login-link" type="button" onClick={() => nav && nav("home")}>
                {tr("Go Home", "Accueil")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page ns-login-page" style={loginPageStyle}>
      <div className="ns-login-shell">
        <section className="ns-login-hero" aria-label={tr("Welcome back to NextScenes", "Bon retour sur NextScenes")}>
          <div>
            <div className="ns-login-eyebrow">{tr("NextScenes Login", "Connexion NextScenes")}</div>
            <h1>{tr("Welcome back", "Bon retour")}</h1>
            <p>
              {tr(
                "Your next chapter starts here.",
                "Votre prochain chapitre commence ici."
              )}
            </p>
          </div>
          <button
            className="btn ns-login-close"
            type="button"
            onClick={handleCancel}
            disabled={busy || forgotBusy || verifyBusy || googleBusy || facebookBusy || appleBusy}
            title={tr("Close", "Fermer")}
            aria-label={tr("Close login", "Fermer la connexion")}
          >
            {tr("Close", "Fermer")}
          </button>
        </section>

        <div className="ns-login-card">
          <div className="ns-login-intro-row">
            <div>
              <h2>{tr("Sign in", "Connexion")}</h2>
              <p>{tr("Choose how to continue.", "Choisissez votre mode de connexion.")}</p>
            </div>
          </div>

          {inviteToken ? (
            <div className="ns-login-invite-note">
              {tr(
                "You’re accepting a Group Writing invitation. Log in to continue.",
                "Vous acceptez une invitation à l’écriture en groupe. Connectez-vous pour continuer."
              )}
            </div>
          ) : null}

          {err ? (
            <div className="banner ns-login-banner">
              {err}
            </div>
          ) : null}

          <div className="ns-login-methods">
            <div className="ns-login-manual-column">
          <form onSubmit={handleSubmit} className="ns-login-form">
            <div className="ns-login-field-block">
              <label className="label">{tr("Username (or Email)", "Nom d’utilisateur (ou email)")}</label>
              <input
                className="input"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                placeholder={tr("buddy.writer", "buddy.writer")}
                autoComplete="username"
                required
                disabled={busy || forgotBusy || verifyBusy || googleBusy || facebookBusy || appleBusy}
              />
            </div>

            <div className="ns-login-password-row">
              <div className="ns-login-field-block">
                <label className="label">{tr("Password", "Mot de passe")}</label>
                <input
                  className="input"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={tr("Your password", "Votre mot de passe")}
                  autoComplete="current-password"
                  required
                  disabled={busy || forgotBusy || verifyBusy || googleBusy || facebookBusy || appleBusy}
                />
              </div>

              <button
                type="button"
                className="linkBtn ns-login-show"
                onClick={() => setShowPw((v) => !v)}
                disabled={busy || forgotBusy || verifyBusy || googleBusy || facebookBusy || appleBusy}
                aria-label={showPw ? tr("Hide password", "Masquer le mot de passe") : tr("Show password", "Afficher le mot de passe")}
                title={showPw ? tr("Hide password", "Masquer") : tr("Show password", "Afficher")}
              >
                {showPw ? tr("Hide", "Masquer") : tr("Show", "Afficher")}
              </button>
            </div>

            <div className="ns-login-actions">
              <button className="btn" type="submit" disabled={!canSubmit}>
                {busy ? tr("Logging in...", "Connexion...") : tr("Login", "Connexion")}
              </button>

              <button
                className="linkBtn ns-login-forgot"
                type="button"
                disabled={busy || forgotBusy || verifyBusy || googleBusy || facebookBusy || appleBusy}
                onClick={() => {
                  setShowForgot((v) => !v);
                  setShowVerifyHelp(false);
                  setForgotMsg("");
                }}
                title={tr("Reset your password", "Réinitialiser votre mot de passe")}
              >
                {tr("Forgot Password?", "Mot de passe oublié ?")}
              </button>
            </div>
          </form>

            </div>
          <div className="ns-login-divider">
            <span>{tr("OR", "OU")}</span>
          </div>

            <div className="ns-login-provider-column">
          {GOOGLE_CLIENT_ID ? (
            <div className="ns-login-google-card">

              <div
                ref={googleBtnRef}
                className="ns-login-google-button"
              />

              {!googleReady && !googleLoadProblem ? (
                <div className="ns-login-loading-note">
                  {tr("Loading Google sign-in…", "Chargement de la connexion Google…")}
                </div>
              ) : null}

              {!googleReady && googleLoadProblem ? (
                <div className="ns-social-provider-fallback" role="status">
                  <span>
                    {googleLoadProblem === "not_configured"
                      ? tr("Google sign-in is not configured yet.", "La connexion Google n’est pas encore configurée.")
                      : tr(
                          "Google sign-in is taking longer than expected. You can retry, use Facebook, or log in with username.",
                          "La connexion Google prend plus de temps que prévu. Vous pouvez réessayer, utiliser Facebook ou vous connecter avec votre nom d’utilisateur."
                        )}
                  </span>
                  {googleLoadProblem !== "not_configured" ? (
                    <button type="button" className="ns-social-retry-button" onClick={retryGoogleSignInLoad}>
                      {tr("Retry Google", "Réessayer Google")}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {FACEBOOK_APP_ID ? (
            <div className="ns-login-facebook-card">

              <button
                className="ns-facebook-login-button"
                type="button"
                onClick={handleFacebookLogin}
                disabled={busy || forgotBusy || verifyBusy || googleBusy || facebookBusy || appleBusy || !facebookReady}
              >
                <span aria-hidden="true">f</span>
                {facebookBusy
                  ? tr("Connecting…", "Connexion…")
                  : tr("Continue with Facebook", "Continuer avec Facebook")}
              </button>

              {!facebookReady && !facebookLoadProblem ? (
                <div className="ns-login-loading-note">
                  {tr("Loading Facebook sign-in…", "Chargement de la connexion Facebook…")}
                </div>
              ) : null}

              {!facebookReady && facebookLoadProblem ? (
                <div className="ns-social-provider-fallback" role="status">
                  <span>
                    {facebookLoadProblem === "not_configured"
                      ? tr("Facebook sign-in is not configured yet.", "La connexion Facebook n’est pas encore configurée.")
                      : tr(
                          "Facebook sign-in is taking longer than expected. You can retry, use Google, or log in with username.",
                          "La connexion Facebook prend plus de temps que prévu. Vous pouvez réessayer, utiliser Google ou vous connecter avec votre nom d’utilisateur."
                        )}
                  </span>
                  {facebookLoadProblem !== "not_configured" ? (
                    <button type="button" className="ns-social-retry-button" onClick={retryFacebookSignInLoad}>
                      {tr("Retry Facebook", "Réessayer Facebook")}
                    </button>
                  ) : null}
                </div>
              ) : null}


            </div>
          ) : null}



          {APPLE_SIGNIN_ENABLED ? (
            <div className="ns-login-facebook-card">

              <button
                className="ns-facebook-login-button"
                type="button"
                onClick={handleAppleLogin}
                disabled={busy || forgotBusy || verifyBusy || googleBusy || facebookBusy || appleBusy}
                style={{ background: "#111827", borderColor: "#111827" }}
              >
                <span aria-hidden="true" style={{ fontFamily: "Georgia, serif", fontWeight: 900 }}></span>
                {appleBusy
                  ? tr("Connecting…", "Connexion…")
                  : tr("Continue with Apple", "Continuer avec Apple")}
              </button>


            </div>
          ) : null}

            </div>
          </div>

          {showVerifyHelp ? (
            <div className="banner ns-login-help-panel">
              <div className="ns-login-panel-title">{tr("Email verification required", "Vérification email requise")}</div>
              <div className="ns-login-muted" style={{ marginTop: 6 }}>
                {tr(
                  "If you didn’t receive the verification email, you can resend it.",
                  "Si vous n’avez pas reçu l’email de vérification, vous pouvez le renvoyer."
                )}
              </div>

              <div className="ns-login-actions ns-login-panel-actions">
                <button className="btn" type="button" onClick={handleResendVerification} disabled={verifyBusy}>
                  {verifyBusy ? tr("Resending...", "Renvoi...") : tr("Resend verification email", "Renvoyer l’email de vérification")}
                </button>

                <button className="linkBtn ns-login-link" type="button" onClick={() => setShowVerifyHelp(false)} disabled={verifyBusy}>
                  {tr("Hide", "Masquer")}
                </button>
              </div>

              {verifyMsg ? <div className="ns-login-message">{verifyMsg}</div> : null}
            </div>
          ) : null}

          {showForgot ? (
            <div className="banner ns-login-help-panel ns-login-forgot-panel">
              <button
                type="button"
                className="linkBtn ns-login-panel-close"
                onClick={() => {
                  setShowForgot(false);
                  setForgotMsg("");
                }}
                disabled={forgotBusy}
                aria-label={tr("Close forgot password panel", "Fermer le panneau mot de passe oublié")}
                title={tr("Close", "Fermer")}
              >
                ✕
              </button>

              <div className="ns-login-panel-title">{tr("Reset your password", "Réinitialiser votre mot de passe")}</div>
              <div className="ns-login-muted" style={{ marginTop: 6 }}>
                {tr(
                  "Type your email in the box above, then send a reset link.",
                  "Saisissez votre email ci-dessus, puis envoyez un lien de réinitialisation."
                )}
              </div>

              <div className="ns-login-actions ns-login-panel-actions">
                <button className="btn" type="button" onClick={handleForgotPassword} disabled={forgotBusy}>
                  {forgotBusy ? tr("Sending...", "Envoi...") : tr("Send reset link", "Envoyer le lien")}
                </button>

                <button className="linkBtn ns-login-link" type="button" onClick={() => setShowForgot(false)} disabled={forgotBusy}>
                  {tr("Hide", "Masquer")}
                </button>
              </div>

              {forgotMsg ? <div className="ns-login-message">{forgotMsg}</div> : null}
            </div>
          ) : null}

          <div className="ns-login-create-row">
            <span>{tr("New to NextScenes?", "Vous découvrez NextScenes ?")}</span>
            <button
              className="linkBtn ns-login-link"
              onClick={() => nav && nav("register")}
              type="button"
              disabled={busy || forgotBusy || verifyBusy || googleBusy || facebookBusy || appleBusy}
            >
              {tr("Create one", "Créer un compte")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
